/**
 * Watchdog · Scoring Dashboard (PRD „Scoring Dashboard" v1.0, 21.08.2026)
 *
 * Zweck: Die Scoring-Ergebnisse aus BigQuery auswerten, ohne in die
 * BigQuery-Konsole zu muessen. Das Werkzeug der Testphase, um Schwaechen in der
 * Systematik zu finden — nicht die Kundensicht (das ist ambix).
 *
 * Wording (🤝 21.08.): Ein SCORINGSET sind alle Zeilen aus
 * `twd_scoring_results` zu EINEM Lauf. Ein Raw-Lauf erzeugt genau ein Set.
 *
 * Der Weg durch die Seite: Domain waehlen → Set waehlen → alles zum Set sehen.
 *
 *   FR-3  Set-Kopf mit Zaehlern, Kategorie-Aufriss und Vollstaendigkeits-Check
 *   FR-4  Ergebnisliste, target-Scorings aufklappbar (sonst stehen bei nkd.de
 *         46 Zeilen conversion_tag_vanished untereinander)
 *   FR-5  Filter nach Ergebnis, Kategorie, Schwere + Freitext
 *
 * Der Vollstaendigkeits-Check ist der wichtigste Teil: Er rechnet gegen den
 * LAUF, nicht gegen eine Katalog-Zahl. Auf zwei Ebenen — sind alle aktiven
 * Pruefungen im Set vertreten, und stimmt die Zeilenzahl mit dem, was das
 * Scanprofil des Laufs erwarten laesst.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Alert, Chip, CircularProgress, Stack, Table, TableHead, TableBody,
  TableRow, TableCell, Collapse, IconButton, TextField, InputAdornment, Tooltip, Divider,
  Select, MenuItem, FormControl, InputLabel, LinearProgress, Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/Storage';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { api } from '../../api/client.js';
import { colors } from '../../styles/theme.js';

/* ── Was vom Server kommt ──────────────────────────────────── */

interface ScoringSet {
  run_ref: string;
  run_at: string;
  domain_ref: string;
  hostname: string | null;
  rows: number;
  passed: number;
  failed: number;
  not_checkable: number;
  scoring_codes: number;
}

interface ResultRow {
  run_ref: string;
  run_at: string;
  domain_ref: string;
  hostname: string | null;
  scoring_code: string;
  scope: string;
  event_ref: string | null;
  target_ref: string | null;
  result: 'passed' | 'failed' | 'not_checkable';
  severity: string;
  category: string;
  details: Record<string, unknown> | null;
}

interface Definition {
  scoring_code: string;
  question: string;
  explanation: string;
  impact_text: string | null;
  category: string;
  severity: string;
  scope: string;
  active: boolean;
  implemented: boolean;
  /** Nummer und Kurzname aus twd_scoring_definitions (P1.1, 28.10.2026); null vor der Migration 20261029_01. */
  display_order: number | null;
  title: string | null;
}

/* ── Darstellung ───────────────────────────────────────────── */

const RESULT_META: Record<ResultRow['result'], { label: string; color: string }> = {
  passed: { label: 'bestanden', color: colors.scoreGreen },
  failed: { label: 'Befund', color: colors.scoreRed },
  not_checkable: { label: 'nicht prüfbar', color: colors.black60 },
};

const CATEGORY_LABEL: Record<string, string> = {
  tracking_quality: 'Tracking-Qualität',
  privacy: 'Datenschutz',
  marketing_tags: 'Marketing-Tags',
};

const SEVERITY_LABEL: Record<string, string> = {
  showstopper: 'Showstopper',
  warning: 'Warning',
  notice: 'Hinweis',
};

/** Die Gründe aus dem details-JSON in Alltagssprache (Bauplan Kap. 2.3). */
const REASON_LABEL: Record<string, string> = {
  no_baseline: 'kein Soll im Scanprofil',
  source_missing: 'Quelle fehlt im Lauf',
  precondition_not_met: 'Vorbedingung nicht erfüllt',
  run_failed: 'Lauf gescheitert',
  site_unreachable: 'ausgesetzt, weil die Seite nicht erreichbar war',
  gtm_missing: 'ausgesetzt, weil kein GTM auf der Seite gefunden wurde',
  detection_uncertain: 'Erkennung unsicher',
};

/**
 * Rückfall-Reihenfolge, solange eine Definition keine Nummer trägt (Migration
 * 20261029_01 noch nicht eingespielt): wie im Bauplan (PRD FR-4). Sonst gilt
 * display_order aus twd_scoring_definitions (P1.1, 28.10.2026).
 */
const CODE_ORDER = [
  'site_unreachable',
  'gtm_removed', 'gtm_container_changed', 'container_version_changed',
  'cmp_removed', 'cmp_changed', 'conversion_tag_vanished',
  'channel_fired', 'conversion_ids_match',
  'como_v2_active', 'como_granted_after_accept', 'como_default_before_gtm',
  'deny_modeling_ping', 'cmp_iab_certified',
];

/** Die drei Scorings, die pro Event × Kanal-Ziel auffächern. */
const TARGET_CODES = ['conversion_tag_vanished', 'channel_fired', 'conversion_ids_match'];

function formatTime(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const setLabel = (s: ScoringSet): string => `${formatTime(s.run_at)} Uhr · ${s.run_ref.slice(0, 8)}`;

/** Ist/Soll und Grund heben wir hervor, der Rest bleibt Key-Value (PRD FR-4). */
const HIGHLIGHT_KEYS = ['soll', 'ist', 'soll_id', 'soll_label', 'reason', 'hinweis'];

export default function ScoringDashboardPage() {
  const [sets, setSets] = useState<ScoringSet[]>([]);
  const [definitions, setDefinitions] = useState<Definition[]>([]);
  const [rows, setRows] = useState<ResultRow[]>([]);
  const [domain, setDomain] = useState<string>('');
  const [runRef, setRunRef] = useState<string>('');
  const [dbProfile, setDbProfile] = useState('');

  const [loading, setLoading] = useState(true);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter (FR-5) — Default: Befunde zuerst.
  const [fResult, setFResult] = useState<'all' | ResultRow['result']>('all');
  const [fCategory, setFCategory] = useState('all');
  const [fSeverity, setFSeverity] = useState('all');
  const [search, setSearch] = useState('');
  const [openCodes, setOpenCodes] = useState<Set<string>>(new Set());

  /** Sets und Definitionen — die Grundlage der Seite. */
  const loadBase = useCallback(async () => {
    setLoading(true);
    const [setsRes, defsRes] = await Promise.all([
      api.get<{ db_profile: string; sets: ScoringSet[] }>('/watchdog/results/sets?limit=100'),
      api.get<{ definitions: Definition[] }>('/watchdog/scoring-definitions'),
    ]);
    setLoading(false);

    if (!setsRes.success) { setError(setsRes.error.message); return; }
    setSets(setsRes.data.sets);
    setDbProfile(setsRes.data.db_profile);
    if (defsRes.success) setDefinitions(defsRes.data.definitions);
    setError(null);

    // Ohne Auswahl das neueste Set zeigen — nach einem manuellen Lauf will man
    // genau das sofort sehen (PRD FR-2).
    const first = setsRes.data.sets[0];
    if (first) {
      setDomain((d) => d || first.domain_ref);
      setRunRef((r) => r || first.run_ref);
    }
  }, []);

  useEffect(() => { void loadBase(); }, [loadBase]);

  /** Die Zeilen des gewählten Sets. */
  useEffect(() => {
    if (!runRef) { setRows([]); return; }
    let abgebrochen = false;
    setRowsLoading(true);
    void api.get<{ results: ResultRow[] }>(`/watchdog/results?run_ref=${runRef}&limit=500`)
      .then((res) => {
        if (abgebrochen) return;
        if (res.success) { setRows(res.data.results); setError(null); }
        else { setRows([]); setError(res.error.message); }
        setRowsLoading(false);
      });
    return () => { abgebrochen = true; };
  }, [runRef]);

  /* ── Auswahl ────────────────────────────────────────────── */

  const domains = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of sets) map.set(s.domain_ref, s.hostname ?? s.domain_ref);
    return [...map.entries()].map(([ref, name]) => ({ ref, name }));
  }, [sets]);

  const setsOfDomain = useMemo(
    () => sets.filter((s) => !domain || s.domain_ref === domain),
    [sets, domain],
  );

  const aktuellesSet = useMemo(
    () => sets.find((s) => s.run_ref === runRef) ?? null,
    [sets, runRef],
  );

  const wechsleDomain = (ref: string) => {
    setDomain(ref);
    const erstes = sets.find((s) => s.domain_ref === ref);
    setRunRef(erstes?.run_ref ?? '');
  };

  /* ── Auswertung des Sets (FR-3) ─────────────────────────── */

  const defByCode = useMemo(
    () => new Map(definitions.map((d) => [d.scoring_code, d])),
    [definitions],
  );

  const proKategorie = useMemo(() => {
    const acc: Record<string, { passed: number; gesamt: number }> = {};
    for (const r of rows) {
      const k = r.category;
      acc[k] ??= { passed: 0, gesamt: 0 };
      acc[k].gesamt += 1;
      if (r.result === 'passed') acc[k].passed += 1;
    }
    return acc;
  }, [rows]);

  const gruende = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const r of rows) {
      if (r.result !== 'not_checkable') continue;
      const grund = String(r.details?.reason ?? 'unbekannt');
      acc[grund] = (acc[grund] ?? 0) + 1;
    }
    return acc;
  }, [rows]);

  /**
   * Vollständigkeits-Check, Prüfungs-Ebene: Fehlt eine aktive Prüfung im Set?
   *
   * Gerechnet gegen die aktiven Definitionen, nicht gegen eine feste Zahl —
   * wer ein Scoring abschaltet, soll keine Warnung sehen.
   */
  const fehlendeCodes = useMemo(() => {
    if (rows.length === 0) return [];
    const imSet = new Set(rows.map((r) => r.scoring_code));
    return definitions.filter((d) => d.active && !imSet.has(d.scoring_code)).map((d) => d.scoring_code);
  }, [rows, definitions]);

  /**
   * Vollständigkeits-Check, Zeilen-Ebene.
   *
   * Die drei target-Scorings fächern über dieselbe Menge Kanal-Ziele auf. Decken
   * sie unterschiedlich viele ab, ist eines davon früh ausgestiegen (meist
   * `source_missing`) — dann ist das Set formal vollständig, inhaltlich aber
   * dünner als es aussieht. Genau das soll sichtbar werden.
   */
  const zielAbdeckung = useMemo(() => {
    const proCode: Record<string, number> = {};
    for (const code of TARGET_CODES) {
      const zeilen = rows.filter((r) => r.scoring_code === code);
      // Zeilen ohne target_ref sind Sammelzeilen (die Regel stieg vorher aus).
      proCode[code] = zeilen.filter((r) => r.target_ref).length;
    }
    const werte = Object.values(proCode);
    const max = Math.max(0, ...werte);
    const abweichend = Object.entries(proCode).filter(([, v]) => v !== max);
    return { proCode, max, abweichend };
  }, [rows]);

  /* ── Gruppierung + Filter (FR-4/FR-5) ───────────────────── */

  const gefiltert = useMemo(() => {
    const n = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (fResult !== 'all' && r.result !== fResult) return false;
      if (fCategory !== 'all' && r.category !== fCategory) return false;
      if (fSeverity !== 'all' && r.severity !== fSeverity) return false;
      if (!n) return true;
      const heu = `${r.scoring_code} ${JSON.stringify(r.details ?? {})}`.toLowerCase();
      return heu.includes(n);
    });
  }, [rows, fResult, fCategory, fSeverity, search]);

  const sektionen = useMemo(() => {
    const map = new Map<string, ResultRow[]>();
    for (const r of gefiltert) {
      const list = map.get(r.scoring_code) ?? [];
      list.push(r);
      map.set(r.scoring_code, list);
    }
    // Nummer aus der Definition zuerst; ohne Nummer die Bauplan-Reihenfolge dahinter.
    const rang = (code: string): number => {
      const nummer = defByCode.get(code)?.display_order;
      if (typeof nummer === 'number') return nummer;
      const i = CODE_ORDER.indexOf(code);
      return i === -1 ? 199 : 100 + i;
    };
    return [...map.entries()].sort((a, b) => rang(a[0]) - rang(b[0]));
  }, [gefiltert, defByCode]);

  const toggleCode = (code: string) => setOpenCodes((prev) => {
    const next = new Set(prev);
    if (next.has(code)) next.delete(code); else next.add(code);
    return next;
  });

  /** Ein details-JSON lesbar machen (PRD FR-4). */
  const renderDetails = (d: Record<string, unknown> | null) => {
    if (!d || Object.keys(d).length === 0) {
      return <Typography variant="caption" color="text.secondary">keine Angaben</Typography>;
    }
    const wichtig = HIGHLIGHT_KEYS.filter((k) => d[k] !== undefined && d[k] !== null);
    const rest = Object.keys(d).filter((k) => !HIGHLIGHT_KEYS.includes(k));

    return (
      <Stack spacing={0.75}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {wichtig.filter((k) => k !== 'reason' && k !== 'hinweis').map((k) => (
            <Box key={k}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                {k === 'ist' ? 'Ist' : k === 'soll' ? 'Soll' : k}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                {typeof d[k] === 'object' ? JSON.stringify(d[k]) : String(d[k])}
              </Typography>
            </Box>
          ))}
          {d.reason !== undefined && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                Grund
              </Typography>
              <Chip
                label={REASON_LABEL[String(d.reason)] ?? String(d.reason)}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.68rem' }}
              />
            </Box>
          )}
        </Stack>

        {d.hinweis !== undefined && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {String(d.hinweis)}
          </Typography>
        )}

        {rest.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
            {rest.map((k) => `${k}: ${typeof d[k] === 'object' ? JSON.stringify(d[k]) : String(d[k])}`).join(' · ')}
          </Typography>
        )}
      </Stack>
    );
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
      {/* ── Kopf ─────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Scoring Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            Ein Scoringset sind alle Bewertungen zu einem Lauf. Domain und Zeitpunkt wählen,
            dann steht hier, was gut lief, was anschlug und was nicht prüfbar war.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {dbProfile && (
            <Chip icon={<StorageIcon sx={{ fontSize: 16 }} />} label={dbProfile} size="small"
              sx={{ fontWeight: 600, bgcolor: colors.lightGrey, border: `1px solid ${colors.silver}` }} />
          )}
          <Tooltip title="Sets neu laden">
            <span>
              <IconButton size="small" onClick={() => void loadBase()} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* ── FR-2: Domain und Set ─────────────────────────── */}
      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }} useFlexGap>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="dom">Domain</InputLabel>
          <Select labelId="dom" label="Domain" value={domain} onChange={(e) => wechsleDomain(e.target.value)}>
            {domains.map((d) => <MenuItem key={d.ref} value={d.ref}>{d.name}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 340 }}>
          <InputLabel id="set">Scoringset</InputLabel>
          <Select labelId="set" label="Scoringset" value={runRef} onChange={(e) => setRunRef(e.target.value)}>
            {setsOfDomain.map((s) => (
              <MenuItem key={s.run_ref} value={s.run_ref}>
                {setLabel(s)} — {s.rows} Zeilen
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : sets.length === 0 ? (
        <Alert severity="info">
          Noch keine Scoringsets in dieser Umgebung. Sie entstehen, sobald ein Score Run gelaufen ist —
          über „Check and Score starten" im Job-Manager.
        </Alert>
      ) : (
        <>
          {/* ── FR-3: Set-Kopf ───────────────────────────── */}
          {aktuellesSet && (
            <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, p: 2, mb: 2 }}>
              <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', mb: 2 }} useFlexGap>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                    Ergebnis des Sets
                  </Typography>
                  <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }}>
                    {(['passed', 'failed', 'not_checkable'] as const).map((k) => (
                      <Chip key={k} size="small" label={`${aktuellesSet[k]} ${RESULT_META[k].label}`}
                        sx={{ bgcolor: RESULT_META[k].color, color: colors.white, fontWeight: 600, fontSize: '0.7rem' }} />
                    ))}
                  </Stack>
                </Box>

                <Divider orientation="vertical" flexItem />

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                    Je Kategorie
                  </Typography>
                  <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                    {Object.entries(proKategorie).map(([k, v]) => (
                      <Typography key={k} variant="body2" sx={{ fontSize: '0.82rem' }}>
                        {CATEGORY_LABEL[k] ?? k}: <strong>{v.passed} von {v.gesamt}</strong> bestanden
                      </Typography>
                    ))}
                  </Stack>
                </Box>

                {Object.keys(gruende).length > 0 && (
                  <>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                        Nicht prüfbar, weil
                      </Typography>
                      <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                        {Object.entries(gruende).map(([g, n]) => (
                          <Chip key={g} size="small" variant="outlined" label={`${REASON_LABEL[g] ?? g}: ${n}`}
                            sx={{ height: 21, fontSize: '0.68rem' }} />
                        ))}
                      </Stack>
                    </Box>
                  </>
                )}
              </Stack>

              {/* Vollständigkeits-Check — zwei Ebenen */}
              {fehlendeCodes.length > 0 ? (
                <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 1 }}>
                  <strong>Set unvollständig:</strong> {fehlendeCodes.join(', ')} fehlt bzw. fehlen.
                  Aktiv sind {definitions.filter((d) => d.active).length} Prüfungen, im Set stehen{' '}
                  {aktuellesSet.scoring_codes}.
                </Alert>
              ) : (
                <Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ mb: 1 }}>
                  Vollständig: {aktuellesSet.scoring_codes} von{' '}
                  {definitions.filter((d) => d.active).length} aktiven Prüfungen im Set, {aktuellesSet.rows} Zeilen.
                </Alert>
              )}

              {zielAbdeckung.abweichend.length > 0 && zielAbdeckung.max > 0 && (
                <Alert severity="info">
                  Ungleiche Ziel-Abdeckung: {zielAbdeckung.abweichend.map(([c, v]) => `${c} deckt ${v}`).join(', ')} von{' '}
                  {zielAbdeckung.max} Kanal-Zielen ab. Die betroffene Prüfung ist früh ausgestiegen — der Grund steht
                  unten an ihrer Zeile.
                </Alert>
              )}
            </Paper>
          )}

          {/* ── FR-5: Filter ─────────────────────────────── */}
          <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center' }} useFlexGap>
            <TextField
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Scoring oder Event suchen …" size="small" sx={{ maxWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: colors.black60 }} /></InputAdornment>
                ),
              }}
            />
            <Chip label="alle" size="small" onClick={() => setFResult('all')}
              variant={fResult === 'all' ? 'filled' : 'outlined'} sx={{ fontWeight: 600 }} />
            {(['failed', 'not_checkable', 'passed'] as const).map((k) => (
              <Chip key={k} size="small" label={RESULT_META[k].label} onClick={() => setFResult(k)}
                variant={fResult === k ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: 600,
                  ...(fResult === k ? { bgcolor: RESULT_META[k].color, color: colors.white } : { borderColor: RESULT_META[k].color }),
                }} />
            ))}

            <FormControl size="small" sx={{ minWidth: 165 }}>
              <InputLabel id="cat">Kategorie</InputLabel>
              <Select labelId="cat" label="Kategorie" value={fCategory} onChange={(e) => setFCategory(e.target.value)}>
                <MenuItem value="all">alle</MenuItem>
                {Object.entries(CATEGORY_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="sev">Schwere</InputLabel>
              <Select labelId="sev" label="Schwere" value={fSeverity} onChange={(e) => setFSeverity(e.target.value)}>
                <MenuItem value="all">alle</MenuItem>
                {Object.entries(SEVERITY_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </Select>
            </FormControl>

            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary">
              {gefiltert.length} von {rows.length} Zeilen
            </Typography>
          </Stack>

          {rowsLoading && <LinearProgress sx={{ mb: 1 }} />}

          {/* ── FR-4: Ergebnisliste ──────────────────────── */}
          <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, overflow: 'hidden' }}>
            {sektionen.length === 0 ? (
              <Box sx={{ p: 5, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {rows.length === 0 ? 'Dieses Set enthält keine Zeilen.' : 'Kein Ergebnis passt zu den Filtern.'}
                </Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableBody>
                  {sektionen.map(([code, zeilen]) => {
                    const def = defByCode.get(code);
                    const istTarget = TARGET_CODES.includes(code) && zeilen.length > 1;
                    const offen = openCodes.has(code);
                    const zaehler = {
                      passed: zeilen.filter((z) => z.result === 'passed').length,
                      failed: zeilen.filter((z) => z.result === 'failed').length,
                      not_checkable: zeilen.filter((z) => z.result === 'not_checkable').length,
                    };
                    // Bei domain-Scorings bestimmt die eine Zeile das Bild.
                    const gesamt: ResultRow['result'] = zaehler.failed > 0
                      ? 'failed'
                      : zaehler.passed > 0 ? 'passed' : 'not_checkable';

                    return (
                      <Fragment key={code}>
                        <TableRow
                          hover
                          sx={{ bgcolor: '#f8fafc', cursor: istTarget ? 'pointer' : 'default' }}
                          onClick={() => istTarget && toggleCode(code)}
                        >
                          <TableCell sx={{ width: 44 }}>
                            {istTarget && (
                              <IconButton size="small">
                                {offen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                              </IconButton>
                            )}
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {def?.title
                                ? (def.display_order ? `${def.display_order}. ${def.title}` : def.title)
                                : def?.question ?? code}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                              {code}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ width: 260 }}>
                            {istTarget ? (
                              <Typography variant="caption">
                                {zeilen.length} Ziele: {zaehler.passed} bestanden · {zaehler.failed} Befund ·{' '}
                                {zaehler.not_checkable} nicht prüfbar
                              </Typography>
                            ) : (
                              <Chip size="small" label={RESULT_META[gesamt].label}
                                sx={{ bgcolor: RESULT_META[gesamt].color, color: colors.white, fontWeight: 600, fontSize: '0.7rem' }} />
                            )}
                          </TableCell>

                          <TableCell sx={{ width: 210 }}>
                            <Typography variant="caption" color="text.secondary">
                              {SEVERITY_LABEL[def?.severity ?? ''] ?? def?.severity ?? ''}
                              {' · '}
                              {CATEGORY_LABEL[def?.category ?? ''] ?? def?.category ?? ''}
                            </Typography>
                          </TableCell>
                        </TableRow>

                        {/* domain-Scoring: die eine Zeile direkt darunter */}
                        {!istTarget && zeilen.map((z, i) => (
                          <TableRow key={`${code}-${i}`}>
                            <TableCell />
                            <TableCell colSpan={3} sx={{ pb: 2 }}>
                              {renderDetails(z.details)}
                              {z.result === 'failed' && def?.impact_text && (
                                <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
                                  <Typography variant="caption">{def.impact_text}</Typography>
                                </Alert>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}

                        {/* target-Scoring: aufklappbar */}
                        {istTarget && (
                          <TableRow>
                            <TableCell colSpan={4} sx={{ py: 0, border: 0 }}>
                              <Collapse in={offen} unmountOnExit>
                                <Box sx={{ py: 1, pl: 5, pr: 2 }}>
                                  {def?.impact_text && zaehler.failed > 0 && (
                                    <Alert severity="error" variant="outlined" sx={{ mb: 1.5 }}>
                                      <Typography variant="caption">{def.impact_text}</Typography>
                                    </Alert>
                                  )}
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem' }}>Ziel</TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', width: 130 }}>Ergebnis</TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem' }}>Belege</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {zeilen.map((z, i) => (
                                        <TableRow key={`${code}-t-${i}`}>
                                          <TableCell sx={{ verticalAlign: 'top' }}>
                                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                              {String(z.details?.event ?? '—')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                              {String(z.details?.kanal ?? '')}
                                            </Typography>
                                          </TableCell>
                                          <TableCell sx={{ verticalAlign: 'top' }}>
                                            <Chip size="small" label={RESULT_META[z.result].label}
                                              sx={{ bgcolor: RESULT_META[z.result].color, color: colors.white, fontWeight: 600, fontSize: '0.66rem', height: 20 }} />
                                          </TableCell>
                                          <TableCell>{renderDetails(z.details)}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Paper>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Quelle: <code>twd_scoring_results</code> (BigQuery), verbunden mit den Texten aus der
            Scoring-Verwaltung. Freigaben laufen weiter über das Notification-Center.
          </Typography>
        </>
      )}
    </Box>
  );
}
